import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  Signal,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { RouterOutlet } from "@angular/router";
import { SideBarComponent } from "../../shared/widgets/side-bar/side-bar.component";
import { ConfirmPopupComponent } from "../../shared/components/confirm-popup/confirm-popup.component";
import { NotificationsStateService } from "../../services/state/notifications-state/notifications-state.service";
import { NotificationsApiService } from "../../services/api/notifications-api/notifications-api.service";
import { SystemAlertService } from "../../services/api/system-alert/sys-alert.service";

import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { NgClass } from "@angular/common";
import { SysAlert } from "../../models/api/sysAlert";
import { first } from "rxjs";
import { PermissionService } from "../../services/api/permission/permission.service";
import { NotificationsFacadeService } from "./facade/notifications-facade.service";

@Component({
  standalone: true,
  selector: "app-dashboard",
  imports: [RouterOutlet, SideBarComponent, ConfirmPopupComponent, NgClass],
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [NotificationsFacadeService],
})
export class DashboardComponent implements OnDestroy {
  private sysAlertService = inject(SystemAlertService);

  public notificationState = inject(NotificationsStateService);
  private notificationsApi = inject(NotificationsApiService);
  private notificationsFacade = inject(NotificationsFacadeService);
  private destroyRef = inject(DestroyRef);

  private sysAlerts: Signal<SysAlert[] | undefined> = toSignal(this.sysAlertService.getAlerts(true));
  public displayedSysAlerts: WritableSignal<SysAlert[]> = signal([]);
  private permissions = inject(PermissionService);

  constructor() {
    effect(() => {
      const sysAlerts = this.sysAlerts();
      if (sysAlerts) {
        const nonReadSysAlerts = sysAlerts.filter((alert) => !this.sysAlertService.getReadStatus(alert));
        this.displayedSysAlerts.set(nonReadSysAlerts);
      }
    });

    if (this.permissions.hasPermission(this.permissions.allPermissions.NOTIFICATION_READ)) {
      this.notificationsApi
        .getNotifications(true, undefined, { pageSize: 1 })
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.notificationState.setUnreadCount(res.meta.totalUnreadCount);
            this.notificationsFacade.start();
          },
          error: () => this.notificationsFacade.start(),
        });
    }
  }

  /**
   * This lifecycle hook is called when the component is destroyed.
   * It cleans up the WebSocket connection to prevent memory leaks.
   */
  public ngOnDestroy() {
    this.notificationsFacade.destroy();
  }

  /**
   * This function is called when the user closes a system alert.
   * It marks the alert as read and removes it from the displayed list.
   * @param alert The system alert to be closed.
   */
  public closeAlert(alert: SysAlert) {
    this.sysAlertService.readAlert(alert);
    this.displayedSysAlerts.update((alerts) => {
      return alerts.filter((a) => a.id !== alert.id);
    });
  }
}
